import { cn } from 'utils/styles';
import Button from 'components/shared/Buttons/BaseButton';
import { formatCompactCurrency, formatCurrency, toCents } from 'components/Widget/shared/currency';
import { getEnrollmentUrl } from 'utils/linkHelpers';
import { ApySavingsBarGraph } from './ApySavingsBarGraph';
import { BREAKDOWN_ROWS, HERO_COMPACT_THRESHOLD, RESULT_COPY, SEGMENT_FILLS } from './constants';
import type { ApySavingsResult } from './types';

interface ApySavingsCalculatorResultProps {
  result: ApySavingsResult;
  periodLabel: string;
}

const RAISED = 'align-super text-heading-sm';

const ROW_LABEL = 'text-body-saans text-content-secondary';

/** Avoid `<sup>` because global styles and assistive technology treat it as a footnote marker. */
const HeroAmount = ({ value }: { value: number }) => {
  const cents = toCents(value);
  const dollars = Math.trunc(cents / 100).toLocaleString('en-US');

  return (
    <p className="text-heading-lg">
      <span className="sr-only">{formatCurrency(value)}</span>
      {value >= HERO_COMPACT_THRESHOLD ? (
        <span aria-hidden>{formatCompactCurrency(value)}</span>
      ) : (
        <span aria-hidden>
          <span className={RAISED}>$</span>
          {dollars}.<span className={RAISED}>{String(cents % 100).padStart(2, '0')}</span>
        </span>
      )}
    </p>
  );
};

export const ApySavingsCalculatorResult = ({ result, periodLabel }: ApySavingsCalculatorResultProps) => (
  <div className="flex flex-col gap-8 rounded-xl bg-bg-primary p-6">
    <div>
      <p className={ROW_LABEL}>{RESULT_COPY.eyebrow(periodLabel)}</p>
      <HeroAmount value={result.totalSavings} />
    </div>

    <ApySavingsBarGraph result={result} />

    {/* The chart's bands print nothing, so these rows and their swatches are the chart's legend. */}
    <dl className="flex flex-col gap-3">
      <div className="flex justify-between gap-4 border-b border-border-primary pb-3 text-label-saans">
        <dt>{RESULT_COPY.breakdownHeading}</dt>
        <dd>{formatCurrency(result.totalSavings)}</dd>
      </div>
      {BREAKDOWN_ROWS.map(row => (
        <div key={row.key} className="flex justify-between gap-4">
          <dt className={ROW_LABEL}>
            <span
              aria-hidden
              className={cn('mr-2 inline-block size-3 rounded-full align-middle', SEGMENT_FILLS[row.key])}
            />
            {row.label}
          </dt>
          <dd className="text-body-saans">{formatCurrency(result[row.key])}</dd>
        </div>
      ))}
    </dl>

    <div className="flex flex-col gap-2">
      <p className="text-label-saans">{RESULT_COPY.ctaHeading}</p>
      <p className={ROW_LABEL}>{RESULT_COPY.ctaBody}</p>
      {/* `Button href` already fires the enrollment CTA event through `Link`; a handler here double-counts. */}
      <Button href={getEnrollmentUrl()} className="mt-2 self-start">
        {RESULT_COPY.ctaLabel}
      </Button>
    </div>
  </div>
);
